import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useConfirm } from '@/components/common/ConfirmDialog'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  defaultDropAnimationSideEffects,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type DraggableSyntheticListeners,
  type DropAnimation,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, useSortable, type SortingStrategy } from '@dnd-kit/sortable'
import { ChevronLeft, ChevronRight, GripVertical, Heading, Info, Pencil, RectangleHorizontal, SeparatorHorizontal, Trash2 } from 'lucide-react'
import { useDeleteField, useUpdateSectionLayout } from '../hooks'
import type { FieldDefinition, FieldType, FormSchema, FormSection } from '../types'
import { PRESENTATIONAL_TYPES } from '../types'
import { FIELD_TYPE_LABELS } from '../types'
import { useAddPresentationalField } from './useAddPresentationalField'

// Blueprint: deep jewel kolor per typ pola. Tailwind v4 wymaga statycznych klas
// (żeby JIT je wyłapał), stąd pełna mapa.
const FIELD_TYPE_STYLE: Record<FieldType, { dot: string; text: string; bar: string }> = {
  TEXT:           { dot: 'bg-blue-900',    text: 'text-blue-900',    bar: 'bg-blue-900' },
  TEXTAREA:       { dot: 'bg-blue-900',    text: 'text-blue-900',    bar: 'bg-blue-900' },
  RICH_TEXT:      { dot: 'bg-blue-900',    text: 'text-blue-900',    bar: 'bg-blue-900' },
  EMAIL:          { dot: 'bg-orange-800',  text: 'text-orange-800',  bar: 'bg-orange-800' },
  PHONE:          { dot: 'bg-orange-800',  text: 'text-orange-800',  bar: 'bg-orange-800' },
  URL:            { dot: 'bg-orange-800',  text: 'text-orange-800',  bar: 'bg-orange-800' },
  IBAN:           { dot: 'bg-orange-800',  text: 'text-orange-800',  bar: 'bg-orange-800' },
  NIP:            { dot: 'bg-orange-800',  text: 'text-orange-800',  bar: 'bg-orange-800' },
  PESEL:          { dot: 'bg-orange-800',  text: 'text-orange-800',  bar: 'bg-orange-800' },
  NUMBER:         { dot: 'bg-emerald-800', text: 'text-emerald-800', bar: 'bg-emerald-800' },
  CURRENCY:       { dot: 'bg-emerald-800', text: 'text-emerald-800', bar: 'bg-emerald-800' },
  DATE:           { dot: 'bg-purple-800',  text: 'text-purple-800',  bar: 'bg-purple-800' },
  BOOLEAN:        { dot: 'bg-indigo-800',  text: 'text-indigo-800',  bar: 'bg-indigo-800' },
  SELECT:         { dot: 'bg-teal-700',    text: 'text-teal-700',    bar: 'bg-teal-700' },
  MULTI_SELECT:   { dot: 'bg-teal-700',    text: 'text-teal-700',    bar: 'bg-teal-700' },
  CHECKBOX_GROUP: { dot: 'bg-teal-700',    text: 'text-teal-700',    bar: 'bg-teal-700' },
  REFERENCE:      { dot: 'bg-teal-700',    text: 'text-teal-700',    bar: 'bg-teal-700' },
  FILE:           { dot: 'bg-rose-800',    text: 'text-rose-800',    bar: 'bg-rose-800' },
  GALLERY:        { dot: 'bg-rose-800',    text: 'text-rose-800',    bar: 'bg-rose-800' },
  SEPARATOR:      { dot: 'bg-slate-500',   text: 'text-slate-500',   bar: 'bg-slate-500' },
  SPACER:         { dot: 'bg-slate-500',   text: 'text-slate-500',   bar: 'bg-slate-500' },
  INSTRUCTION:    { dot: 'bg-slate-500',   text: 'text-slate-500',   bar: 'bg-slate-500' },
  HEADER:         { dot: 'bg-slate-700',   text: 'text-slate-700',   bar: 'bg-slate-700' },
}

function fieldStyle(t: FieldType | string) {
  return FIELD_TYPE_STYLE[t as FieldType] ?? FIELD_TYPE_STYLE.TEXT
}

type Props = {
  schema: FormSchema
  section: FormSection
  objectType: string
  onAddField: () => void
  onEditField: (field: FieldDefinition) => void
}

const WIDTH_OPTIONS = [3, 4, 6, 8, 9, 12]
const OVERLAY_WIDTH_PX = 300

// CSS grid + różne `gridColumn: span N` źle współgra z rectSortingStrategy
// (transformy swap-u rectów o różnych szerokościach jitterują). No-op + DragOverlay
// + dashed slot na pozycji źródłowej + isOver highlight na karcie docelowej dają
// czystą wizualnie pracę bez setState w trakcie pointermove.
const disabledSortingStrategy: SortingStrategy = () => null

const dropAnimation: DropAnimation = {
  duration: 180,
  easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: '0.4' } },
  }),
}

type PlacedField = FieldDefinition & { sortOrder: number; colWidth: number }

function clampWidth(w: number | undefined): number {
  if (!w || w < 1) return 12
  if (w > 12) return 12
  return w
}

function nextWidth(current: number, dir: -1 | 1): number {
  const idx = WIDTH_OPTIONS.indexOf(current)
  const start = idx === -1 ? WIDTH_OPTIONS.indexOf(12) : idx
  const nextIdx = Math.max(0, Math.min(WIDTH_OPTIONS.length - 1, start + dir))
  return WIDTH_OPTIONS[nextIdx]
}

export function SectionLayoutEditor({ schema, section, objectType, onAddField, onEditField }: Props) {
  const confirm = useConfirm()
  const del = useDeleteField(objectType)
  const updateLayout = useUpdateSectionLayout(objectType)
  const addPresent = useAddPresentationalField(objectType)

  // Local copy that re-syncs when the server data changes. Mutations during drag/resize
  // happen optimistically here and persist on commit.
  // Synchronizujemy po treści (klucz string), nie po referencji array - inaczej
  // przy każdym re-renderze parenta z nową referencją schema.fields useEffect
  // wywołałby setFields i wpadlibyśmy w pętlę.
  // Layout (sortOrder/colWidth) pochodzi z placements, nie z FieldDefinition.
  const initial = useMemo((): PlacedField[] => {
    const fieldById = new Map(schema.fields.map(f => [f.id, f]))
    return (schema.placements ?? [])
      .filter(p => p.sectionId === section.id)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(p => {
        const field = fieldById.get(p.fieldId)
        return field ? { ...field, sortOrder: p.sortOrder, colWidth: p.colWidth } : null
      })
      .filter((x): x is PlacedField => x !== null)
  }, [schema.fields, schema.placements, section.id])
  const initialKey = useMemo(
    () => initial.map(f => `${f.fieldCode}:${f.sortOrder}:${f.colWidth}`).join('|'),
    [initial],
  )
  const [fields, setFields] = useState<PlacedField[]>(initial)
  const fieldsRef = useRef(fields)
  fieldsRef.current = fields
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFields(initial) }, [initialKey])

  const [activeFieldCode, setActiveFieldCode] = useState<string | null>(null)
  const activeField = activeFieldCode
    ? fields.find(f => f.fieldCode === activeFieldCode) ?? null
    : null

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  const persistLayout = useCallback((next: PlacedField[]) => {
    const entries = next.map((f, i) => ({
      fieldCode: f.fieldCode,
      sortOrder: i * 10,
      colWidth: clampWidth(f.colWidth),
    }))
    updateLayout.mutate({ sectionCode: section.code, entries })
  }, [updateLayout, section.code])

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveFieldCode(String(event.active.id))
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveFieldCode(null)
    const { active, over } = event
    if (!over || active.id === over.id) return
    const current = fieldsRef.current
    const oldIdx = current.findIndex(f => f.fieldCode === active.id)
    const newIdx = current.findIndex(f => f.fieldCode === over.id)
    if (oldIdx === -1 || newIdx === -1) return
    const next = arrayMove(current, oldIdx, newIdx)
    setFields(next)
    persistLayout(next)
  }, [persistLayout])

  const handleDragCancel = useCallback(() => {
    setActiveFieldCode(null)
  }, [])

  // Stabilne callbacki - referencje stałe, więc memoizowane FieldCardy nie re-rendrują
  // się przy każdym render parenta. Aktualne `fields`, `onEditField`, mutacje czytane
  // przez ref/closure.
  const handleEditField = useCallback((fieldCode: string) => {
    const f = fieldsRef.current.find(x => x.fieldCode === fieldCode)
    if (f) onEditField(f)
  }, [onEditField])

  const delMutateRef = useRef(del.mutate)
  delMutateRef.current = del.mutate
  const confirmRef = useRef(confirm)
  confirmRef.current = confirm
  const handleDeleteField = useCallback(async (fieldCode: string) => {
    const ok = await confirmRef.current({
      title: `Usunąć pole ${fieldCode}?`,
      description: 'Operacja jest nieodwracalna.',
      confirmLabel: 'Usuń',
      variant: 'destructive',
    })
    if (ok) delMutateRef.current(fieldCode, { onError: (e) => toast.error(formatError(e)) })
  }, [])

  const handleResize = useCallback((fieldCode: string, dir: -1 | 1) => {
    const next = fieldsRef.current.map(f =>
      f.fieldCode === fieldCode
        ? { ...f, colWidth: nextWidth(clampWidth(f.colWidth), dir) }
        : f,
    )
    setFields(next)
    persistLayout(next)
  }, [persistLayout])

  return (
    <main className="flex-1 flex flex-col overflow-hidden">
      <header className="px-4 py-3 border-b flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">{section.labelPl}</h2>
          <p className="text-xs text-slate-500 font-mono">
            {section.code} · siatka 12-kolumnowa · przeciągnij aby zmienić kolejność, użyj strzałek aby zmienić szerokość
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => addPresent.add('SEPARATOR', section)}
            disabled={addPresent.isPending}
            className="rounded border border-slate-300 text-slate-600 p-1.5 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50"
            title="Separator - linia oddzielająca grupę pól"
            aria-label="Dodaj separator"
          >
            <SeparatorHorizontal size={16} />
          </button>
          <button
            type="button"
            onClick={() => addPresent.add('SPACER', section)}
            disabled={addPresent.isPending}
            className="rounded border border-slate-300 text-slate-600 p-1.5 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50"
            title="Pusta przestrzeń - puste miejsce wypełniające kolumny"
            aria-label="Dodaj pustą przestrzeń"
          >
            <RectangleHorizontal size={16} />
          </button>
          <button
            type="button"
            onClick={() => addPresent.add('INSTRUCTION', section)}
            disabled={addPresent.isPending}
            className="rounded border border-slate-300 text-slate-600 p-1.5 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50"
            title="Instrukcja - info-box z treścią"
            aria-label="Dodaj instrukcję"
          >
            <Info size={16} />
          </button>
          <button
            type="button"
            onClick={() => addPresent.add('HEADER', section)}
            disabled={addPresent.isPending}
            className="rounded border border-slate-300 text-slate-600 p-1.5 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50"
            title="Nagłówek - tytuł grupujący kolejne pola"
            aria-label="Dodaj nagłówek"
          >
            <Heading size={16} />
          </button>
          <div className="w-px self-stretch bg-slate-200 mx-0.5" />
          <button
            type="button"
            onClick={onAddField}
            className="rounded bg-sky-600 text-white px-3 py-1.5 text-sm whitespace-nowrap hover:bg-sky-700"
          >
            + Dodaj pole
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-auto px-4 py-3">
        {fields.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Brak pól w tej sekcji. Kliknij "+ Dodaj pole".</p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext items={fields.map(f => f.fieldCode)} strategy={disabledSortingStrategy}>
              <div className="grid grid-cols-12 gap-2">
                {fields.map(f => (
                  <FieldCard
                    key={f.fieldCode}
                    field={f}
                    onEdit={handleEditField}
                    onDelete={handleDeleteField}
                    onResize={handleResize}
                  />
                ))}
              </div>
            </SortableContext>
            <DragOverlay dropAnimation={dropAnimation} zIndex={100}>
              {activeField ? <FieldCardOverlay field={activeField} /> : null}
            </DragOverlay>
          </DndContext>
        )}
        {updateLayout.isPending && (
          <div className="mt-2 text-xs text-slate-500">Zapisywanie układu...</div>
        )}
        {updateLayout.isError && (
          <div className="mt-2 text-xs text-red-600">Błąd zapisu układu - spróbuj ponownie.</div>
        )}
      </div>
    </main>
  )
}

type FieldCardProps = {
  field: PlacedField
  onEdit: (fieldCode: string) => void
  onDelete: (fieldCode: string) => void
  onResize: (fieldCode: string, dir: -1 | 1) => void
}

const FieldCard = memo(function FieldCard({ field, onEdit, onDelete, onResize }: FieldCardProps) {
  const { attributes, listeners, setNodeRef, isDragging, isOver, activeIndex, index } = useSortable({
    id: field.fieldCode,
  })
  const width = clampWidth(field.colWidth)

  // Pasek wstawienia po lewej/prawej. arrayMove(arr, old, new) wkłada element
  // tak, że jeśli active był PRZED over - po move ląduje ZA over (right);
  // jeśli active był ZA over - po move ląduje PRZED over (left).
  const showInsertBar = isOver && activeIndex >= 0 && activeIndex !== index
  const insertSide: 'left' | 'right' | null = !showInsertBar
    ? null
    : activeIndex < index
      ? 'right'
      : 'left'

  // Jeden root <div> dla obu stanów (placeholder vs full): inaczej React unmountuje
  // element przy toggle isDragging, a useSortable.setNodeRef podpina się do nowego
  // węzła - w StrictMode cleanup tego efektu wpadał w setState pętlę.
  const containerClass = isDragging
    ? 'relative rounded-lg border-2 border-dashed border-slate-300 bg-slate-50/70 min-h-[96px] flex items-center justify-center select-none'
    : 'relative rounded-lg border border-slate-200 bg-white p-2 flex flex-col gap-1 shadow-sm hover:shadow-md transition-shadow'

  return (
    <div
      ref={setNodeRef}
      style={{ gridColumn: `span ${width}` }}
      className={containerClass}
      {...attributes}
    >
      {insertSide === 'left' && <InsertBar side="left" />}
      {insertSide === 'right' && <InsertBar side="right" />}
      {isDragging ? (
        <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider pointer-events-none">
          tu było
        </span>
      ) : (
        <FieldCardBody
          field={field}
          dragListeners={listeners}
          onEdit={() => onEdit(field.fieldCode)}
          onDelete={() => onDelete(field.fieldCode)}
          onResize={(dir) => onResize(field.fieldCode, dir)}
        />
      )}
    </div>
  )
})

function InsertBar({ side }: { side: 'left' | 'right' }) {
  return (
    <div
      aria-hidden
      className={[
        'absolute top-0 bottom-0 w-1 bg-sky-500 rounded-full shadow-[0_0_8px_rgba(14,165,233,0.6)] pointer-events-none',
        side === 'left' ? '-left-1.5' : '-right-1.5',
      ].join(' ')}
    />
  )
}

function FieldCardOverlay({ field }: { field: PlacedField }) {
  return (
    <div
      style={{
        width: OVERLAY_WIDTH_PX,
        transform: 'rotate(-1.5deg)',
      }}
      className="rounded-lg border-2 border-sky-500 bg-white p-2.5 flex flex-col gap-1.5 shadow-2xl shadow-sky-500/25 ring-4 ring-sky-100/60 cursor-grabbing"
    >
      <FieldCardBody field={field} />
    </div>
  )
}

function FieldCardBody({
  field,
  dragListeners,
  onEdit,
  onDelete,
  onResize,
}: {
  field: PlacedField
  dragListeners?: DraggableSyntheticListeners
  onEdit?: () => void
  onDelete?: () => void
  onResize?: (dir: -1 | 1) => void
}) {
  const width = clampWidth(field.colWidth)
  const style = fieldStyle(field.fieldType)
  return (
    <>
      <div className="flex items-start justify-between gap-1">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700 -ml-1 mt-0.5 shrink-0"
          aria-label="Przeciągnij"
          {...(dragListeners ?? {})}
        >
          <GripVertical size={14} />
        </button>
        <div className="flex-1 min-w-0">
          {field.fieldType === 'SEPARATOR' ? (
            <hr className="my-2 border-t border-slate-300" />
          ) : field.fieldType === 'INSTRUCTION' ? (
            <div className="text-[11px] text-slate-600 leading-snug line-clamp-2" title={field.helpText ?? ''}>
              {field.helpText?.trim() || <span className="italic text-slate-400">Pusta instrukcja</span>}
            </div>
          ) : (
            <>
              <div className="text-[13px] font-semibold text-slate-900 truncate leading-tight" title={field.labelPl}>
                {field.labelPl}
                {field.isRequired && <span className="text-rose-700 ml-0.5">*</span>}
              </div>
              {!PRESENTATIONAL_TYPES.includes(field.fieldType as FieldType) && (
                <div className="text-[10px] text-slate-500 font-mono truncate mt-0.5" title={field.fieldCode}>
                  {field.fieldCode}
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            onClick={onEdit}
            disabled={!onEdit}
            className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-teal-700 transition-colors"
            aria-label="Edytuj"
          >
            <Pencil size={12} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={!onDelete}
            className="p-1 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-700 transition-colors"
            aria-label="Usuń"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 mt-1.5">
        <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono font-semibold uppercase tracking-wider ${style.text} truncate`}>
          <span className={`size-1.5 rounded-full ${style.dot} shrink-0`} />
          {FIELD_TYPE_LABELS[field.fieldType as FieldType] ?? field.fieldType}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onResize ? (e) => { e.stopPropagation(); onResize(-1) } : undefined}
            disabled={!onResize || width <= WIDTH_OPTIONS[0]}
            className="p-0.5 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Zwęź"
          >
            <ChevronLeft size={12} />
          </button>
          <WidthScale width={width} barColor={style.bar} />
          <button
            type="button"
            onClick={onResize ? (e) => { e.stopPropagation(); onResize(1) } : undefined}
            disabled={!onResize || width >= WIDTH_OPTIONS[WIDTH_OPTIONS.length - 1]}
            className="p-0.5 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Rozszerz"
          >
            <ChevronRight size={12} />
          </button>
        </div>
      </div>
      {field.isRequired ? (
        <div className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.08em] text-rose-700">
          <span className="size-1 rounded-full bg-rose-700" />
          wymagane
        </div>
      ) : (
        <div className="text-[9px] font-medium uppercase tracking-[0.08em] text-slate-400">
          opcjonalne
        </div>
      )}
    </>
  )
}

function WidthScale({ width, barColor }: { width: number; barColor: string }) {
  return (
    <div className="flex items-center gap-[1.5px]" title={`${width}/12`}>
      {Array.from({ length: 12 }, (_, i) => (
        <span
          key={i}
          className={`h-2.5 w-[3px] rounded-[1px] ${i < width ? barColor : 'bg-slate-200'}`}
        />
      ))}
    </div>
  )
}

function formatError(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string; title?: string } } }
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? 'Błąd'
}
